import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { useRecaptcha } from "@/hooks/use-recaptcha";

const initial = { name: "", email: "", company: "", role: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const { enabled: captchaEnabled, execute: executeCaptcha } = useRecaptcha("lead_submit");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Please fill in your name, work email and company.");
      return;
    }
    setLoading(true);
    try {
      let captchaToken = null;
      try {
        captchaToken = await executeCaptcha();
      } catch {
        // Google unreachable (blocked, offline, ad blocker). Stop here rather
        // than posting a token-less request the backend will reject anyway.
        setError(
          "Could not complete the spam check. Please disable any blockers, or email us at hello@zaplee.ai.",
        );
        toast.error("Spam check unavailable.");
        setLoading(false);
        return;
      }

      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        role: form.role.trim() || null,
        message: form.message.trim() || null,
        captcha_token: captchaToken,
      });
      setSent(true);
      setForm(initial);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      setError(errorMessage(err));
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 md:py-32 bg-[#F8F9FA] border-t border-zinc-200"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <span className="font-mono-label text-[10px] text-[#0047FF]">
              // Talk to us
            </span>
            <h2 className="font-display mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
              See Zaplee.ai on your own claims.
            </h2>
            <p className="mt-5 text-lg text-zinc-600 max-w-md">
              Tell us a bit about your practice or billing company. We&apos;ll show
              you exactly where revenue is leaking — usually in 30 minutes.
            </p>

            <div className="mt-10 space-y-4">
              {[
                "30-minute live walkthrough",
                "Custom revenue leak analysis",
                "ROI projection for your stack",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3">
                  <CheckCircle2 size={18} className="text-[#0047FF]" />
                  <span className="text-sm text-zinc-700">{t}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex items-center gap-3 text-sm text-zinc-600">
              <Mail size={16} />
              <a
                href="mailto:hello@zaplee.ai"
                className="hover:text-zinc-950 underline-offset-4 hover:underline"
              >
                hello@zaplee.ai
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-7 sm:p-10 shadow-[0_30px_80px_-40px_rgba(9,9,11,0.25)]">
            {sent ? (
              <div
                data-testid={CONTACT.success}
                className="text-center py-10"
              >
                <div className="mx-auto h-12 w-12 rounded-full bg-[#0047FF]/10 text-[#0047FF] grid place-items-center">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="font-display mt-5 text-2xl font-semibold tracking-tight text-zinc-950">
                  Thanks — message received.
                </h3>
                <p className="mt-3 text-zinc-600">
                  We&apos;ll reach out within one business day to schedule your walkthrough.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSent(false)}
                  className="mt-6 rounded-full"
                  data-testid="contact-send-another"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <form
                data-testid={CONTACT.form}
                onSubmit={onSubmit}
                className="space-y-5"
                noValidate
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-zinc-800">
                      Full name
                    </Label>
                    <Input
                      id="name"
                      data-testid={CONTACT.inputName}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Cooper"
                      className="mt-2 h-11"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-zinc-800">
                      Work email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid={CONTACT.inputEmail}
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jane@practice.com"
                      className="mt-2 h-11"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="company" className="text-zinc-800">
                      Practice or billing company
                    </Label>
                    <Input
                      id="company"
                      data-testid={CONTACT.inputCompany}
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Northwind Health"
                      className="mt-2 h-11"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="role" className="text-zinc-800">
                      Role <span className="text-zinc-400">(optional)</span>
                    </Label>
                    <Input
                      id="role"
                      data-testid={CONTACT.inputRole}
                      value={form.role}
                      onChange={update("role")}
                      placeholder="Director of Operations"
                      className="mt-2 h-11"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-zinc-800">
                    What&apos;s on your mind? <span className="text-zinc-400">(optional)</span>
                  </Label>
                  <Textarea
                    id="message"
                    data-testid={CONTACT.inputMessage}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your current RCM workflow and what you'd like to improve…"
                    className="mt-2 min-h-[120px]"
                  />
                </div>

                {error && (
                  <p
                    data-testid={CONTACT.error}
                    className="text-sm text-red-600"
                  >
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={loading}
                  className="w-full sm:w-auto bg-zinc-950 text-white hover:bg-zinc-800 rounded-full h-12 px-7"
                >
                  {loading ? "Sending…" : "Book my demo"}
                  {!loading && <ArrowRight size={16} className="ml-1.5" />}
                </Button>

                <p className="text-xs text-zinc-500">
                  By submitting you agree to our privacy policy. We will never share your data.
                </p>

                {/* Google requires this attribution whenever the v3 badge is hidden. */}
                {captchaEnabled && (
                  <p className="text-[11px] leading-relaxed text-zinc-400">
                    Protected by reCAPTCHA — Google&apos;s{" "}
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-zinc-600"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="https://policies.google.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-zinc-600"
                    >
                      Terms of Service
                    </a>{" "}
                    apply.
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
